/** @module ProductComplianceSystem */
import crews from "csi/access/crews";
import { Action } from "csi/def/Action";
import { Context } from "csi/def/Context";
import csi from "csi/main";
import reflection from "csi/reflection";
import _ from "lodash";
import { C8Node, DUMMY } from "src/c8-platform/type/node";

const OBSOLETED_LABEL_ITEM_STATUS = "LabelItemCalculationStatus:Obsoleted";
const PRODUCT_COMPLIANCE_ANALYSIS_ADMINISTRATOR = "Product Compliance Analysis Administrator";
const PRODUCT_COMPLIANCE_ANALYSIS_EDITOR = "Product Compliance Analysis Editor";

/**
 * Food Label > Compliance Analysis Action is enabled only when the user has "Product Compliance Analysis Administrator" security role
 * @param {*} ctx
 * @param {*} action
 * @returns: boolean
 */
export function onAccessFoodLabelComplianceAnalysis(ctx: Context, action: Action): boolean {
	const fldsNode = ctx.getContextNode();
	const fldsRC = reflection.getClass(fldsNode.$Type);

	if (!fldsRC.isDerivedFrom("FoodLabelDataSheetRevision")) {
		return false;
	}

	return crews.isInRoles([PRODUCT_COMPLIANCE_ANALYSIS_ADMINISTRATOR]) && hasValidComplianceAnalysisTypeAndConnector(ctx, fldsNode) && hasValidFlatIngredientItem(ctx, fldsNode);
}

/**
 * FlatIngredientItem > Bypass Reason Action is shown only when the Parent Product of the FlatIngredient has Compliance Analysis Types selected
 * @param {*} ctx
 * @returns: boolean
 */
export function onAccessFlatIngredientItemBypassAction(ctx: Context): boolean {
	const flatIngNode = ctx.getContextNode();
	const fldsRevNode = ctx.cache.getNode(flatIngNode.__Parent__);

	return !!getAnalysisType(ctx, fldsRevNode);
}

/**
 * Determine if a given FLDS Revision node has a populated ComplianceAnalysisTypes list
 * @param {Context} ctx
 * @returns: boolean
 */
function hasValidComplianceAnalysisTypeAndConnector(ctx: Context, fldsNode: C8Node): boolean {
	const analysisType = getAnalysisType(ctx, fldsNode);
	return analysisType && analysisType.ComplianceSystemConnector != DUMMY;
}

/**
 * Determine if product settings have Compliance Analysis Types selected
 * @param {Context} ctx
 * @returns: boolean
 */
function getAnalysisType(ctx: Context, fldsNode: C8Node): C8Node {
	const parentProduct = ctx.cache.getNode(fldsNode.ParentProduct);
	const parentProductRC = reflection.getClass(parentProduct?.$Type);

	let analysisType = null;
	if (parentProductRC.isDerivedFrom("FormulatedProduct")) {
		const subtype = ctx.cache.getNode(parentProduct?.Subtype);
		const settings = ctx.cache.getNode(subtype?.Settings);

		if (_.isEmpty(settings?.ComplianceAnalysisTypes)) {
			return null;
		} else {
			analysisType = ctx.cache.getNode(settings?.ComplianceAnalysisTypes[0]);
		}
	}
	else if (parentProductRC.isDerivedFrom("Product")) {
		const productType = ctx.cache.getNode(parentProduct?.ProductType);
		const productionConfig = ctx.cache.getNode(productType?.ProductionConfig);

		if (_.isEmpty(productionConfig?.ComplianceAnalysisTypes)) {
			return null;
		} else {
			analysisType = ctx.cache.getNode(productionConfig?.ComplianceAnalysisTypes[0]);
		}
	}

	return analysisType;
}

function hasValidFlatIngredientItem(ctx: Context, fldsNode: C8Node): boolean {
	const uniqueFlatIngredients = ctx.cache.getNodes(fldsNode.UniqueFlatIngredients);

	if (!_.isEmpty(uniqueFlatIngredients)) {
		for (const flatIngredient of uniqueFlatIngredients) {
			const calculationStatus = flatIngredient?.CalculationStatus;
			const bypassReason = flatIngredient?.ProductComplianceAnalysisBypassReason;
			
			if (calculationStatus !== OBSOLETED_LABEL_ITEM_STATUS && bypassReason === csi.EMPTY_STRING) {
				return true;
			} 
		}
	}

	return false;
}

/**
 * NOTE - Function commented out in C8FB-2896 as it is not currently used, but this may change in future refinement
 *        stories 
 * 
 * Disallows editing on BaseProduct.ComplianceSystemUsage if user does not have right to edit Product Compliance Analysis
 * @param {*} ctx
 * @param {*} action
 * @returns
 */
// export function onAccessComplianceSystemUsage(ctx: Context, action: Action): boolean {
// 	return crews.isInRoles([PRODUCT_COMPLIANCE_ANALYSIS_EDITOR, PRODUCT_COMPLIANCE_ANALYSIS_ADMINISTRATOR]);
// }
